import { Button, Container, Element, Label } from '@playcanvas/pcui';

import type { Annotation, AnnotationLayer } from '../annotations';
import { Events } from '../events';
import sectionAnnotationSvg from './svg/section-annotation.svg';

type AnnotationType = Annotation['type'];

type CameraPose = {
    position?: { x: number; y: number; z: number };
    target?: { x: number; y: number; z: number };
};

type Group = {
    id: string;
    name: string;
    annotations: Annotation[];
};

const TYPE_OPTIONS: AnnotationType[] = ['pin', 'evidence', 'witness', 'vehicle', 'poi', 'damage'];
const TYPE_LABELS: Record<AnnotationType, string> = {
    pin: '📍',
    evidence: '🧾',
    witness: '👤',
    vehicle: '🚗',
    poi: '📌',
    damage: '⚠️'
};

const normalizeText = (value: unknown) => {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const createLayerId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `layer-${crypto.randomUUID().slice(0, 8)}`;
    }

    return `layer-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

class AnnotationPanel extends Container {
    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'annotation-panel',
            class: 'panel',
            hidden: true
        };

        super(args);

        this.style.top = '50%';
        this.style.transform = 'translate(0, -50%)';
        this.style.right = '102px';
        this.style.width = '360px';
        this.style.maxHeight = '75%';
        this.style.flexDirection = 'column';
        this.style.overflow = 'hidden';

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        const header = new Container({
            class: 'panel-header'
        });

        const iconWrapper = document.createElement('div');
        iconWrapper.classList.add('panel-header-icon');
        const decodedSvg = decodeURIComponent(sectionAnnotationSvg.replace(/^data:[^,]+,/, ''));
        iconWrapper.appendChild(new DOMParser().parseFromString(decodedSvg, 'image/svg+xml').documentElement);
        const icon = new Element({ dom: iconWrapper });

        const label = new Label({
            text: 'Annotations',
            class: 'panel-header-label'
        });

        header.append(icon);
        header.append(label);

        const body = new Container({
            id: 'annotation-panel-body'
        });
        body.style.display = 'flex';
        body.style.flexDirection = 'column';
        body.style.gap = '8px';
        body.style.padding = '8px';
        body.style.overflow = 'hidden';

        const layerControls = document.createElement('div');
        layerControls.id = 'annotation-panel-layer-controls';
        layerControls.style.display = 'flex';
        layerControls.style.flexDirection = 'column';
        layerControls.style.gap = '6px';

        const layerTogglesList = document.createElement('div');
        layerTogglesList.id = 'annotation-panel-layer-toggle-list';
        layerTogglesList.style.display = 'flex';
        layerTogglesList.style.flexDirection = 'column';
        layerTogglesList.style.gap = '4px';
        layerTogglesList.style.maxHeight = '88px';
        layerTogglesList.style.overflowY = 'auto';

        const addLayerButton = document.createElement('button');
        addLayerButton.type = 'button';
        addLayerButton.id = 'annotation-panel-add-layer';
        addLayerButton.textContent = 'Add Layer';

        layerControls.appendChild(layerTogglesList);
        layerControls.appendChild(addLayerButton);

        const list = document.createElement('div');
        list.id = 'annotation-panel-list';
        list.style.display = 'flex';
        list.style.flexDirection = 'column';
        list.style.gap = '8px';
        list.style.flex = '1';
        list.style.overflowY = 'auto';

        body.dom.appendChild(layerControls);
        body.dom.appendChild(list);

        this.append(header);
        this.append(body);

        let annotations = (events.invoke('annotations.list') as Annotation[]) ?? [];
        let layers = (events.invoke('annotations.layers') as AnnotationLayer[]) ?? [];
        let selectedId = String(events.invoke('annotations.selected') ?? '') || null;
        let editingId: string | null = null;

        const getAnnotation = (id: string | null) => {
            if (!id) {
                return null;
            }
            return annotations.find(annotation => annotation.id === id) ?? null;
        };

        const focusAnnotation = (annotation: Annotation) => {
            const pose = (events.invoke('camera.getPose') as CameraPose | undefined) ?? {};
            const position = pose.position;
            const target = pose.target;

            const pointX = annotation.worldPosition[0];
            const pointY = annotation.worldPosition[1];
            const pointZ = annotation.worldPosition[2];

            const dirX = (target?.x ?? pointX) - (position?.x ?? pointX);
            const dirY = (target?.y ?? pointY) - (position?.y ?? pointY);
            const dirZ = (target?.z ?? (pointZ - 1)) - (position?.z ?? pointZ);
            const dirLen = Math.hypot(dirX, dirY, dirZ);

            const normX = dirLen > 1e-5 ? dirX / dirLen : 0;
            const normY = dirLen > 1e-5 ? dirY / dirLen : 0;
            const normZ = dirLen > 1e-5 ? dirZ / dirLen : -1;

            const currentDistance = dirLen > 0 ? dirLen : 4;
            const focusDistance = Math.max(1.5, Math.min(8, currentDistance * 0.35));

            events.fire('camera.setPose', {
                position: {
                    x: pointX - normX * focusDistance,
                    y: pointY - normY * focusDistance,
                    z: pointZ - normZ * focusDistance
                },
                target: {
                    x: pointX,
                    y: pointY,
                    z: pointZ
                }
            }, 1);
        };

        // Forward-declare so helpers defined before renderList can reference it.
        let renderList: () => void; // eslint-disable-line prefer-const

        const selectAnnotation = (id: string) => {
            const annotation = getAnnotation(id);
            if (!annotation) {
                return;
            }

            events.fire('annotations.select', id);
            focusAnnotation(annotation);
            selectedId = id;
            renderList();
        };

        const createGroups = () => {
            const layerMap = new Map<string, Group>();
            const groups: Group[] = [];

            for (const layer of layers) {
                const group: Group = {
                    id: layer.id,
                    name: layer.name || layer.id,
                    annotations: []
                };
                layerMap.set(layer.id, group);
                groups.push(group);
            }

            let unassignedGroup: Group | null = null;

            for (const annotation of annotations) {
                const layerId = annotation.layerId;
                if (!layerId) {
                    if (!unassignedGroup) {
                        unassignedGroup = {
                            id: '__unassigned__',
                            name: 'Unassigned',
                            annotations: []
                        };
                        groups.push(unassignedGroup);
                    }
                    unassignedGroup.annotations.push(annotation);
                    continue;
                }

                const existing = layerMap.get(layerId);
                if (existing) {
                    existing.annotations.push(annotation);
                    continue;
                }

                const fallback: Group = {
                    id: layerId,
                    name: layerId,
                    annotations: [annotation]
                };
                layerMap.set(layerId, fallback);
                groups.push(fallback);
            }

            return groups;
        };

        const startInlineRename = (
            container: HTMLElement,
            currentValue: string,
            onCommit: (value: string) => void
        ) => {
            const input = document.createElement('input');
            input.type = 'text';
            input.value = currentValue;
            input.maxLength = 256;
            input.style.width = '100%';

            const originalContent = container.textContent;
            container.textContent = '';
            container.appendChild(input);
            input.focus();
            input.select();

            const commit = () => {
                const trimmed = input.value.trim();
                if (trimmed && trimmed !== currentValue) {
                    onCommit(trimmed);
                } else {
                    container.textContent = originalContent;
                }
            };

            input.addEventListener('blur', commit, { once: true });
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    input.blur();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    input.removeEventListener('blur', commit);
                    container.textContent = originalContent;
                }
            });
        };

        const renderLayerControls = () => {
            while (layerTogglesList.firstChild) {
                layerTogglesList.removeChild(layerTogglesList.firstChild);
            }

            if (layers.length === 0) {
                const empty = document.createElement('div');
                empty.textContent = 'No layers yet';
                empty.style.opacity = '0.75';
                empty.style.fontSize = '11px';
                layerTogglesList.appendChild(empty);
                return;
            }

            const fragment = document.createDocumentFragment();

            for (const layer of layers) {
                const row = document.createElement('label');
                row.style.display = 'flex';
                row.style.alignItems = 'center';
                row.style.gap = '6px';
                row.style.cursor = 'pointer';

                const toggle = document.createElement('input');
                toggle.type = 'checkbox';
                toggle.checked = !!layer.visible;
                toggle.dataset.annotationLayerToggle = layer.id;
                toggle.addEventListener('change', () => {
                    events.fire('annotations.layers.setVisible', layer.id, toggle.checked);
                });

                const text = document.createElement('span');
                text.dataset.annotationLayerName = layer.id;
                text.textContent = layer.name || layer.id;
                text.style.flex = '1';

                // Double-click to rename layer
                text.addEventListener('dblclick', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    startInlineRename(text, layer.name || layer.id, (newName) => {
                        events.fire('annotations.layers.rename', layer.id, newName);
                    });
                });

                row.appendChild(toggle);
                row.appendChild(text);
                fragment.appendChild(row);
            }

            layerTogglesList.appendChild(fragment);
        };

        const createMeta = (annotation: Annotation) => {
            const exhibit = normalizeText(annotation.exhibitId) ?? 'No exhibit';
            const author = normalizeText(annotation.author) ?? 'Unknown';
            const createdAt = normalizeText(annotation.createdAt) ?? 'Unknown date';
            return `${exhibit} • ${author} • ${createdAt}`;
        };

        const buildEditForm = (annotation: Annotation) => {
            const editWrap = document.createElement('div');
            editWrap.dataset.annotationEditForm = annotation.id;
            editWrap.style.display = 'flex';
            editWrap.style.flexDirection = 'column';
            editWrap.style.gap = '6px';
            editWrap.style.padding = '8px';
            editWrap.style.border = '1px solid rgba(255, 255, 255, 0.14)';
            editWrap.style.borderRadius = '4px';
            editWrap.style.marginTop = '6px';

            const typeInput = document.createElement('select');
            typeInput.id = `annotation-panel-edit-type-${annotation.id}`;
            typeInput.dataset.annotationEditType = annotation.id;
            for (const optionType of TYPE_OPTIONS) {
                const option = document.createElement('option');
                option.value = optionType;
                option.textContent = optionType;
                option.selected = optionType === annotation.type;
                typeInput.appendChild(option);
            }

            const labelInput = document.createElement('input');
            labelInput.type = 'text';
            labelInput.value = annotation.label ?? '';
            labelInput.maxLength = 256;
            labelInput.id = `annotation-panel-edit-label-${annotation.id}`;
            labelInput.dataset.annotationEditLabel = annotation.id;
            labelInput.placeholder = 'Label';

            const exhibitInput = document.createElement('input');
            exhibitInput.type = 'text';
            exhibitInput.value = annotation.exhibitId ?? '';
            exhibitInput.id = `annotation-panel-edit-exhibit-${annotation.id}`;
            exhibitInput.dataset.annotationEditExhibit = annotation.id;
            exhibitInput.placeholder = 'Exhibit ID';

            const descriptionInput = document.createElement('textarea');
            descriptionInput.value = annotation.description ?? '';
            descriptionInput.id = `annotation-panel-edit-description-${annotation.id}`;
            descriptionInput.dataset.annotationEditDescription = annotation.id;
            descriptionInput.placeholder = 'Description';
            descriptionInput.rows = 3;

            const validation = document.createElement('div');
            validation.dataset.annotationEditValidation = annotation.id;
            validation.style.fontSize = '11px';
            validation.style.color = '#ff8e8e';
            validation.textContent = '';

            const actions = document.createElement('div');
            actions.style.display = 'flex';
            actions.style.gap = '6px';

            const save = document.createElement('button');
            save.type = 'button';
            save.textContent = 'Save';
            save.dataset.annotationEditSave = annotation.id;

            const cancel = document.createElement('button');
            cancel.type = 'button';
            cancel.textContent = 'Cancel';
            cancel.dataset.annotationEditCancel = annotation.id;

            const updateSaveState = () => {
                const labelValue = labelInput.value.trim();
                const typeValue = typeInput.value as AnnotationType;

                if (!labelValue) {
                    save.disabled = true;
                    validation.textContent = 'Label is required.';
                    return;
                }

                if (!TYPE_OPTIONS.includes(typeValue)) {
                    save.disabled = true;
                    validation.textContent = 'Type is invalid.';
                    return;
                }

                save.disabled = false;
                validation.textContent = '';
            };

            typeInput.addEventListener('change', updateSaveState);
            labelInput.addEventListener('input', updateSaveState);

            save.addEventListener('click', () => {
                const labelValue = labelInput.value.trim();
                const typeValue = typeInput.value as AnnotationType;
                if (!labelValue || !TYPE_OPTIONS.includes(typeValue)) {
                    updateSaveState();
                    return;
                }

                events.fire('annotations.update', {
                    id: annotation.id,
                    patch: {
                        type: typeValue,
                        label: labelValue,
                        description: normalizeText(descriptionInput.value),
                        exhibitId: normalizeText(exhibitInput.value)
                    }
                });

                editingId = null;
                renderList();
            });

            cancel.addEventListener('click', () => {
                editingId = null;
                renderList();
            });

            actions.appendChild(save);
            actions.appendChild(cancel);

            editWrap.appendChild(typeInput);
            editWrap.appendChild(labelInput);
            editWrap.appendChild(exhibitInput);
            editWrap.appendChild(descriptionInput);
            editWrap.appendChild(validation);
            editWrap.appendChild(actions);

            updateSaveState();

            return editWrap;
        };

        renderList = () => {
            while (list.firstChild) {
                list.removeChild(list.firstChild);
            }

            const groups = createGroups();

            if (groups.length === 0) {
                const empty = document.createElement('div');
                empty.id = 'annotation-panel-empty';
                empty.textContent = 'No annotations yet';
                empty.style.opacity = '0.75';
                list.appendChild(empty);
                return;
            }

            const fragment = document.createDocumentFragment();

            for (const group of groups) {
                const groupWrap = document.createElement('section');
                groupWrap.dataset.annotationLayerGroup = group.id;
                groupWrap.style.display = 'flex';
                groupWrap.style.flexDirection = 'column';
                groupWrap.style.gap = '6px';

                const groupHeader = document.createElement('div');
                groupHeader.dataset.annotationLayerGroupHeader = group.id;
                groupHeader.className = 'annotation-layer-group-header';
                groupHeader.textContent = `${group.name} (${group.annotations.length})`;
                groupHeader.style.fontWeight = 'bold';
                groupHeader.style.opacity = '0.9';
                groupHeader.style.padding = '4px 6px';
                groupHeader.style.borderRadius = '4px';

                // Double-click to rename layer (not for __unassigned__)
                if (group.id !== '__unassigned__') {
                    groupHeader.addEventListener('dblclick', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        startInlineRename(groupHeader, group.name, (newName) => {
                            events.fire('annotations.layers.rename', group.id, newName);
                        });
                    });
                }

                // Drop target for drag-and-drop
                groupHeader.addEventListener('dragover', (e) => {
                    e.preventDefault();
                    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
                    groupHeader.classList.add('annotation-layer-drop-target');
                });
                groupHeader.addEventListener('dragleave', () => {
                    groupHeader.classList.remove('annotation-layer-drop-target');
                });
                groupHeader.addEventListener('drop', (e) => {
                    e.preventDefault();
                    groupHeader.classList.remove('annotation-layer-drop-target');
                    const annotationId = e.dataTransfer?.getData('text/annotation-id');
                    if (!annotationId) return;
                    const targetLayerId = group.id === '__unassigned__' ? undefined : group.id;
                    events.fire('annotations.update', {
                        id: annotationId,
                        patch: { layerId: targetLayerId }
                    });
                });

                groupWrap.appendChild(groupHeader);

                if (group.annotations.length === 0) {
                    const empty = document.createElement('div');
                    empty.textContent = 'No annotations in this layer';
                    empty.style.opacity = '0.7';
                    empty.style.fontSize = '11px';
                    groupWrap.appendChild(empty);
                    fragment.appendChild(groupWrap);
                    continue;
                }

                for (const annotation of group.annotations) {
                    const item = document.createElement('div');
                    item.dataset.annotationItemId = annotation.id;
                    item.className = 'annotation-panel-item';
                    item.style.display = 'flex';
                    item.style.flexDirection = 'column';
                    item.style.gap = '4px';
                    item.style.padding = '7px';
                    item.style.borderRadius = '4px';
                    item.style.border = '1px solid rgba(255, 255, 255, 0.16)';
                    item.style.background = annotation.id === selectedId ? 'rgba(255, 255, 255, 0.08)' : 'transparent';
                    item.style.cursor = 'pointer';

                    // Drag support
                    item.draggable = true;
                    item.addEventListener('dragstart', (e) => {
                        e.dataTransfer?.setData('text/annotation-id', annotation.id);
                        if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
                        item.style.opacity = '0.5';
                    });
                    item.addEventListener('dragend', () => {
                        item.style.opacity = '';
                    });

                    // Hover events for cross-highlighting
                    item.addEventListener('pointerenter', () => {
                        events.fire('annotations.hover', annotation.id);
                    });
                    item.addEventListener('pointerleave', () => {
                        events.fire('annotations.hover', null);
                    });

                    item.addEventListener('click', () => {
                        selectAnnotation(annotation.id);
                    });

                    const title = document.createElement('div');
                    title.style.display = 'flex';
                    title.style.gap = '8px';
                    title.style.alignItems = 'center';

                    const typeIcon = document.createElement('span');
                    typeIcon.dataset.annotationType = annotation.id;
                    typeIcon.textContent = TYPE_LABELS[annotation.type] ?? '•';

                    const titleText = document.createElement('span');
                    titleText.dataset.annotationLabel = annotation.id;
                    titleText.textContent = normalizeText(annotation.label) ?? 'Annotation';
                    titleText.style.fontWeight = 'bold';
                    titleText.style.flex = '1';

                    // Double-click to rename annotation
                    titleText.addEventListener('dblclick', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        startInlineRename(titleText, annotation.label || '', (newName) => {
                            events.fire('annotations.update', {
                                id: annotation.id,
                                patch: { label: newName }
                            });
                        });
                    });

                    title.appendChild(typeIcon);
                    title.appendChild(titleText);

                    const meta = document.createElement('div');
                    meta.dataset.annotationMeta = annotation.id;
                    meta.textContent = createMeta(annotation);
                    meta.style.opacity = '0.8';
                    meta.style.fontSize = '11px';

                    const actions = document.createElement('div');
                    actions.style.display = 'flex';
                    actions.style.gap = '6px';

                    const editButton = document.createElement('button');
                    editButton.type = 'button';
                    editButton.textContent = 'Edit';
                    editButton.dataset.annotationEdit = annotation.id;
                    // eslint-disable-next-line no-loop-func
                    editButton.addEventListener('click', (event) => {
                        event.stopPropagation();
                        editingId = annotation.id;
                        renderList();
                    });

                    const deleteButton = document.createElement('button');
                    deleteButton.type = 'button';
                    deleteButton.textContent = 'Delete';
                    deleteButton.dataset.annotationDelete = annotation.id;
                    // eslint-disable-next-line no-loop-func
                    deleteButton.addEventListener('click', (event) => {
                        event.stopPropagation();
                        const confirmed = window.confirm('Delete this annotation?'); // eslint-disable-line no-alert
                        if (!confirmed) {
                            return;
                        }
                        events.fire('annotations.remove', annotation.id);
                        if (selectedId === annotation.id) {
                            selectedId = null;
                        }
                        if (editingId === annotation.id) {
                            editingId = null;
                        }
                        renderList();
                    });

                    actions.appendChild(editButton);
                    actions.appendChild(deleteButton);

                    item.appendChild(title);
                    item.appendChild(meta);
                    item.appendChild(actions);

                    if (editingId === annotation.id) {
                        item.appendChild(buildEditForm(annotation));
                    }

                    groupWrap.appendChild(item);
                }

                fragment.appendChild(groupWrap);
            }

            list.appendChild(fragment);
        };

        const refresh = () => {
            annotations = (events.invoke('annotations.list') as Annotation[]) ?? [];
            layers = (events.invoke('annotations.layers') as AnnotationLayer[]) ?? [];
            selectedId = String(events.invoke('annotations.selected') ?? '') || null;
            if (editingId && !annotations.some(annotation => annotation.id === editingId)) {
                editingId = null;
            }
            renderLayerControls();
            renderList();
        };

        const setVisible = (visible: boolean) => {
            if (visible === this.hidden) {
                if (visible) {
                    events.fire('viewPanel.setVisible', false);
                    events.fire('colorPanel.setVisible', false);
                    events.fire('casePanel.setVisible', false);
                    events.fire('evidencePanel.setVisible', false);
                }

                this.hidden = !visible;
                events.fire('annotationPanel.visible', visible);
            }
        };

        let addLayerInputVisible = false;

        const showAddLayerInput = () => {
            if (addLayerInputVisible) return;
            addLayerInputVisible = true;

            const inputRow = document.createElement('div');
            inputRow.style.display = 'flex';
            inputRow.style.gap = '4px';

            const input = document.createElement('input');
            input.type = 'text';
            input.placeholder = 'Layer name';
            input.maxLength = 256;
            input.style.flex = '1';

            const confirmBtn = document.createElement('button');
            confirmBtn.type = 'button';
            confirmBtn.textContent = 'OK';

            const cancelBtn = document.createElement('button');
            cancelBtn.type = 'button';
            cancelBtn.textContent = 'Cancel';

            inputRow.appendChild(input);
            inputRow.appendChild(confirmBtn);
            inputRow.appendChild(cancelBtn);

            addLayerButton.style.display = 'none';
            layerControls.appendChild(inputRow);
            input.focus();

            const cleanup = () => {
                addLayerInputVisible = false;
                inputRow.remove();
                addLayerButton.style.display = '';
            };

            const commit = () => {
                const trimmedName = normalizeText(input.value);
                if (trimmedName) {
                    events.fire('annotations.layers.add', {
                        id: createLayerId(),
                        name: trimmedName,
                        visible: true,
                        locked: false
                    } as AnnotationLayer);
                }
                cleanup();
            };

            confirmBtn.addEventListener('click', commit);
            cancelBtn.addEventListener('click', cleanup);
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    commit();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cleanup();
                }
            });
        };

        addLayerButton.addEventListener('click', showAddLayerInput);

        events.function('annotationPanel.visible', () => {
            return !this.hidden;
        });

        events.on('annotationPanel.setVisible', (visible: boolean) => {
            setVisible(visible);
        });

        events.on('annotationPanel.toggleVisible', () => {
            setVisible(this.hidden);
        });

        events.on('annotations.listChanged', () => {
            refresh();
        });

        events.on('annotations.layersChanged', () => {
            refresh();
        });

        events.on('annotations.select', () => {
            selectedId = String(events.invoke('annotations.selected') ?? '') || null;
            renderList();
        });

        events.on('viewPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        events.on('colorPanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        events.on('casePanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        events.on('evidencePanel.visible', (visible: boolean) => {
            if (visible) {
                setVisible(false);
            }
        });

        refresh();
    }
}

export { AnnotationPanel };
